import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { CreditCardIcon, UsersIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, AlertTriangleIcon, CalendarIcon, TrendingUpIcon, FilterIcon, CheckCircleIcon, StoreIcon, PercentIcon, MapPinIcon, BarChart2Icon, ShoppingBagIcon, ClockIcon } from 'lucide-react';
import Card from '../components/ui/Card';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { pakistaniCities } from '../utils/dataGenerators';
// Sample data for the dashboard
const merchantGrowthData = [{
  name: 'Jan',
  newMerchants: 42,
  churnedMerchants: 8
}, {
  name: 'Feb',
  newMerchants: 38,
  churnedMerchants: 5
}, {
  name: 'Mar',
  newMerchants: 45,
  churnedMerchants: 7
}, {
  name: 'Apr',
  newMerchants: 52,
  churnedMerchants: 6
}, {
  name: 'May',
  newMerchants: 48,
  churnedMerchants: 9
}, {
  name: 'Jun',
  newMerchants: 56,
  churnedMerchants: 4
}];
const transactionVolumeData = [{
  name: 'Jan',
  volume: 18500000
}, {
  name: 'Feb',
  volume: 22300000
}, {
  name: 'Mar',
  volume: 21800000
}, {
  name: 'Apr',
  volume: 24500000
}, {
  name: 'May',
  volume: 28900000
}, {
  name: 'Jun',
  volume: 32100000
}];
const merchantCategoryData = [{
  name: 'Banking',
  value: 35
}, {
  name: 'Microfinance',
  value: 25
}, {
  name: 'Insurance',
  value: 20
}, {
  name: 'Retail',
  value: 15
}, {
  name: 'Others',
  value: 5
}];
const merchantStatusData = [{
  name: 'Active',
  value: 78
}, {
  name: 'Inactive',
  value: 12
}, {
  name: 'Pending',
  value: 10
}];
const topMerchantsByVolume = [{
  name: 'Habib Bank Limited',
  volume: 5840000,
  growth: '+12%'
}, {
  name: 'Meezan Bank',
  volume: 4920000,
  growth: '+8%'
}, {
  name: 'MCB Bank Limited',
  volume: 4380000,
  growth: '+5%'
}, {
  name: 'Bank Alfalah',
  volume: 3760000,
  growth: '+9%'
}, {
  name: 'Telenor Microfinance Bank',
  volume: 2950000,
  growth: '+15%'
}];
const regionPerformanceData = pakistaniCities.slice(0, 5).map(city => ({
  name: city,
  merchants: Math.floor(Math.random() * 50) + 20,
  volume: (Math.random() * 15000000 + 5000000).toFixed(0)
}));
const mdrRevenueData = [{
  name: 'Jan',
  revenue: 925000
}, {
  name: 'Feb',
  revenue: 1115000
}, {
  name: 'Mar',
  revenue: 1090000
}, {
  name: 'Apr',
  revenue: 1225000
}, {
  name: 'May',
  revenue: 1445000
}, {
  name: 'Jun',
  revenue: 1605000
}];
const COLORS = ['#4F46E5', '#F59E0B', '#EF4444', '#10B981', '#8B5CF6'];
const Dashboard: React.FC = () => {
  const {
    user
  } = useAuth();
  const [timeRange, setTimeRange] = useState('month');
  const [chartView, setChartView] = useState({
    merchants: '6months',
    volume: '6months'
  });
  const [merchantCount, setMerchantCount] = useState({
    total: 0,
    active: 0,
    new: 0
  });
  const [transactionStats, setTransactionStats] = useState({
    total: 0,
    volume: 0,
    success: 0
  });
  // Simulate loading data
  useEffect(() => {
    // In a real app, this would be an API call
    setMerchantCount({
      total: 1247,
      active: 972,
      new: 56
    });
    setTransactionStats({
      total: 287542,
      volume: 32100000,
      success: 98.2
    });
  }, [timeRange]);
  // Format currency for tooltips and displays
  const formatCurrency = (value: number) => {
    return `PKR ${value.toLocaleString()}`;
  };
  return <div className="space-y-6" data-id="element-434">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white p-6 rounded-lg shadow-sm border border-gray-200" data-id="element-435">
        <div data-id="element-436">
          <h1 className="text-2xl font-semibold text-gray-800 mb-1" data-id="element-437">
            Merchant Management System
          </h1>
          <p className="text-gray-600" data-id="element-438">
            Welcome back, <span className="font-medium" data-id="element-439">{user?.username}</span>
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4" data-id="element-440">
          <div className="flex items-center space-x-2" data-id="element-441">
            <CalendarIcon className="h-4 w-4 text-blue-600" data-id="element-442" />
            <Select options={[{
            value: 'today',
            label: 'Today'
          }, {
            value: 'week',
            label: 'This Week'
          }, {
            value: 'month',
            label: 'This Month'
          }, {
            value: 'quarter',
            label: 'This Quarter'
          }, {
            value: 'year',
            label: 'This Year'
          }]} value={timeRange} onChange={e => setTimeRange(e.target.value)} className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-w-[140px]" data-id="element-443" />
          </div>
          <Button variant="outline" size="sm" icon={<FilterIcon className="h-4 w-4" data-id="element-445" />} data-id="element-444">
            Filters
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-446">
        <StatCard title="Total Merchants" value={merchantCount.total.toLocaleString()} change="+8.2%" isPositive={true} icon={<StoreIcon className="h-6 w-6 text-blue-600" data-id="element-448" />} data-id="element-447" />
        <StatCard title="Transaction Volume" value={`PKR ${(transactionStats.volume / 1000000).toFixed(1)}M`} change="+11.3%" isPositive={true} icon={<DollarSignIcon className="h-6 w-6 text-green-600" data-id="element-450" />} data-id="element-449" />
        <StatCard title="Success Rate" value={`${transactionStats.success}%`} change="+0.7%" isPositive={true} icon={<CheckCircleIcon className="h-6 w-6 text-emerald-600" data-id="element-452" />} data-id="element-451" />
        <StatCard title="New Merchants" value={merchantCount.new.toString()} change="+16.7%" isPositive={true} icon={<UsersIcon className="h-6 w-6 text-purple-600" data-id="element-454" />} data-id="element-453" />
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-455">
        {/* Merchant Growth */}
        <Card title="Merchant Acquisition & Retention" actions={<div className="flex space-x-1" data-id="element-457">
              <Button variant={chartView.merchants === '6months' ? 'primary' : 'ghost'} size="sm" onClick={() => setChartView({
          ...chartView,
          merchants: '6months'
        })} data-id="element-458">
                6 Months
              </Button>
              <Button variant={chartView.merchants === 'ytd' ? 'primary' : 'ghost'} size="sm" onClick={() => setChartView({
          ...chartView,
          merchants: 'ytd'
        })} data-id="element-459">
                YTD
              </Button>
            </div>} data-id="element-456">
          <div className="h-80" data-id="element-460">
            <ResponsiveContainer width="100%" height="100%" data-id="element-461">
              <BarChart data={merchantGrowthData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-462">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" data-id="element-463" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-464" />
                <YAxis axisLine={false} tickLine={false} data-id="element-465" />
                <Tooltip contentStyle={{
                backgroundColor: 'white',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb'
              }} data-id="element-466" />
                <Legend data-id="element-467" />
                <Bar name="New Merchants" dataKey="newMerchants" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-468" />
                <Bar name="Churned Merchants" dataKey="churnedMerchants" fill="#EF4444" radius={[4, 4, 0, 0]} data-id="element-469" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Transaction Volume Trends */}
        <Card title="Transaction Volume Trends" actions={<div className="flex space-x-1" data-id="element-471">
              <Button variant={chartView.volume === '6months' ? 'primary' : 'ghost'} size="sm" onClick={() => setChartView({
          ...chartView,
          volume: '6months'
        })} data-id="element-472">
                6 Months
              </Button>
              <Button variant={chartView.volume === 'ytd' ? 'primary' : 'ghost'} size="sm" onClick={() => setChartView({
          ...chartView,
          volume: 'ytd'
        })} data-id="element-473">
                YTD
              </Button>
            </div>} data-id="element-470">
          <div className="h-80" data-id="element-474">
            <ResponsiveContainer width="100%" height="100%" data-id="element-475">
              <AreaChart data={transactionVolumeData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-476">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" data-id="element-477" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-478" />
                <YAxis axisLine={false} tickLine={false} data-id="element-479" />
                <Tooltip formatter={value => [formatCurrency(Number(value)), 'Volume']} contentStyle={{
                backgroundColor: 'white',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb'
              }} data-id="element-480" />
                <defs data-id="element-481">
                  <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1" data-id="element-482">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8} data-id="element-483" />
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} data-id="element-484" />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="volume" stroke="#4F46E5" strokeWidth={2} fillOpacity={1} fill="url(#colorVolume)" data-id="element-485" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-486">
        {/* Merchant Categories */}
        <Card title="Merchant Categories" data-id="element-487">
          <div className="h-64" data-id="element-488">
            <ResponsiveContainer width="100%" height="100%" data-id="element-489">
              <PieChart data-id="element-490">
                <Pie data={merchantCategoryData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                name,
                percent
              }) => `${name} ${(percent * 100).toFixed(0)}%`} data-id="element-491">
                  {merchantCategoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-492" />)}
                </Pie>
                <Tooltip formatter={value => [`${value}%`, 'Percentage']} data-id="element-493" />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2" data-id="element-494">
            {merchantCategoryData.map((entry, index) => <div key={index} className="flex items-center" data-id="element-495">
                <div className="w-3 h-3 rounded-full mr-2" style={{
              backgroundColor: COLORS[index % COLORS.length]
            }} data-id="element-496"></div>
                <span className="text-xs text-gray-600" data-id="element-497">{entry.name}</span>
              </div>)}
          </div>
        </Card>

        {/* Merchant Status */}
        <Card title="Merchant Status" data-id="element-498">
          <div className="h-64" data-id="element-499">
            <ResponsiveContainer width="100%" height="100%" data-id="element-500">
              <PieChart data-id="element-501">
                <Pie data={merchantStatusData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                name,
                percent
              }) => `${name} ${(percent * 100).toFixed(0)}%`} data-id="element-502">
                  <Cell fill="#10B981" data-id="element-503" />
                  <Cell fill="#F59E0B" data-id="element-504" />
                  <Cell fill="#6B7280" data-id="element-505" />
                </Pie>
                <Tooltip formatter={value => [`${value}%`, 'Percentage']} data-id="element-506" />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2" data-id="element-507">
            <div className="flex items-center" data-id="element-508">
              <div className="w-3 h-3 rounded-full mr-2 bg-emerald-500" data-id="element-509"></div>
              <span className="text-xs text-gray-600" data-id="element-510">Active</span>
            </div>
            <div className="flex items-center" data-id="element-511">
              <div className="w-3 h-3 rounded-full mr-2 bg-amber-500" data-id="element-512"></div>
              <span className="text-xs text-gray-600" data-id="element-513">Inactive</span>
            </div>
            <div className="flex items-center" data-id="element-514">
              <div className="w-3 h-3 rounded-full mr-2 bg-gray-500" data-id="element-515"></div>
              <span className="text-xs text-gray-600" data-id="element-516">Pending</span>
            </div>
          </div>
        </Card>

        {/* MDR Revenue */}
        <Card title="MDR Revenue" data-id="element-517">
          <div className="h-64" data-id="element-518">
            <ResponsiveContainer width="100%" height="100%" data-id="element-519">
              <LineChart data={mdrRevenueData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-520">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" data-id="element-521" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-522" />
                <YAxis axisLine={false} tickLine={false} data-id="element-523" />
                <Tooltip formatter={value => [formatCurrency(Number(value)), 'Revenue']} contentStyle={{
                backgroundColor: 'white',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb'
              }} data-id="element-524" />
                <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={{
                r: 4,
                fill: '#10B981'
              }} activeDot={{
                r: 6,
                stroke: 'white',
                strokeWidth: 2
              }} data-id="element-525" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Top Merchants & Regional Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-526">
        {/* Top Merchants by Volume */}
        <Card title="Top Merchants by Volume" icon={<BarChart2Icon className="h-5 w-5" data-id="element-528" />} actions={<Button variant="ghost" size="sm" data-id="element-529">
              View All
            </Button>} data-id="element-527">
          <div className="space-y-5" data-id="element-530">
            {topMerchantsByVolume.map((merchant, index) => <div key={index} className="flex items-center" data-id="element-531">
                <div className="flex-shrink-0 w-8 text-center" data-id="element-532">
                  <span className="font-semibold text-gray-500" data-id="element-533">
                    #{index + 1}
                  </span>
                </div>
                <div className="ml-3 flex-1" data-id="element-534">
                  <div className="flex justify-between items-center mb-1" data-id="element-535">
                    <span className="text-sm font-medium text-gray-900" data-id="element-536">
                      {merchant.name}
                    </span>
                    <div className="flex items-center" data-id="element-537">
                      <span className="text-sm font-medium text-gray-900 mr-2" data-id="element-538">
                        {formatCurrency(merchant.volume)}
                      </span>
                      <Badge variant="success" size="sm" data-id="element-539">
                        {merchant.growth}
                      </Badge>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden" data-id="element-540">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-700 h-2 rounded-full transition-all duration-500 ease-in-out" style={{
                  width: `${merchant.volume / topMerchantsByVolume[0].volume * 100}%`
                }} data-id="element-541"></div>
                  </div>
                </div>
              </div>)}
          </div>
        </Card>

        {/* Regional Performance */}
        <Card title="Regional Performance" icon={<MapPinIcon className="h-5 w-5" data-id="element-543" />} actions={<Button variant="ghost" size="sm" data-id="element-544">
              View Map
            </Button>} data-id="element-542">
          <div className="space-y-5" data-id="element-545">
            {regionPerformanceData.map((region, index) => <div key={index} className="flex items-center" data-id="element-546">
                <div className="flex-shrink-0" data-id="element-547">
                  <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-548">
                    <MapPinIcon className="h-5 w-5 text-blue-600" data-id="element-549" />
                  </div>
                </div>
                <div className="ml-3 flex-1" data-id="element-550">
                  <div className="flex justify-between items-center mb-1" data-id="element-551">
                    <span className="text-sm font-medium text-gray-900" data-id="element-552">
                      {region.name}
                    </span>
                    <div className="text-right" data-id="element-553">
                      <div className="text-sm font-medium text-gray-900" data-id="element-554">
                        {formatCurrency(parseInt(region.volume))}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-555">
                        {region.merchants} merchants
                      </div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden" data-id="element-556">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-700 h-2 rounded-full transition-all duration-500 ease-in-out" style={{
                  width: `${parseInt(region.volume) / parseInt(regionPerformanceData[0].volume) * 100}%`
                }} data-id="element-557"></div>
                  </div>
                </div>
              </div>)}
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card title="Recent Activity" actions={<Button variant="ghost" size="sm" data-id="element-559">
            View All
          </Button>} noPadding data-id="element-558">
        <div className="divide-y divide-gray-100" data-id="element-560">
          {[{
          icon: <StoreIcon className="h-4 w-4 text-blue-600" data-id="element-561" />,
          title: 'New merchant registered',
          description: 'Business name: Habib Bank Limited',
          time: '2 hours ago',
          bgColor: 'bg-blue-100'
        }, {
          icon: <CreditCardIcon className="h-4 w-4 text-green-600" data-id="element-562" />,
          title: 'Large transaction processed',
          description: 'Amount: PKR 1,250,000',
          time: '3 hours ago',
          bgColor: 'bg-green-100'
        }, {
          icon: <PercentIcon className="h-4 w-4 text-purple-600" data-id="element-563" />,
          title: 'MDR rate updated',
          description: 'For merchant: Bank Alfalah',
          time: '5 hours ago',
          bgColor: 'bg-purple-100'
        }, {
          icon: <AlertTriangleIcon className="h-4 w-4 text-red-600" data-id="element-564" />,
          title: 'Merchant flagged for review',
          description: 'Business name: MCB Bank Limited',
          time: '6 hours ago',
          bgColor: 'bg-red-100'
        }, {
          icon: <ShoppingBagIcon className="h-4 w-4 text-amber-600" data-id="element-565" />,
          title: 'New service activated',
          description: 'For merchant: Telenor Microfinance Bank',
          time: '8 hours ago',
          bgColor: 'bg-amber-100'
        }].map((activity, index) => <div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors duration-150" data-id="element-566">
              <div className="flex items-center" data-id="element-567">
                <div className="flex-shrink-0" data-id="element-568">
                  <div className={`h-9 w-9 rounded-full ${activity.bgColor} flex items-center justify-center shadow-sm`} data-id="element-569">
                    {activity.icon}
                  </div>
                </div>
                <div className="ml-4 flex-1" data-id="element-570">
                  <div className="flex items-center justify-between" data-id="element-571">
                    <div data-id="element-572">
                      <p className="text-sm font-medium text-gray-900" data-id="element-573">
                        {activity.title}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-574">
                        {activity.description}
                      </p>
                    </div>
                    <Badge variant="secondary" size="sm" data-id="element-575">
                      {activity.time}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </Card>
    </div>;
};
interface StatCardProps {
  title: string;
  value: string;
  change: string;
  isPositive: boolean;
  icon: React.ReactNode;
}
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  isPositive,
  icon
}) => {
  return <Card className="transition-all duration-200 hover:shadow-md" data-id="element-576">
      <div className="flex items-center" data-id="element-577">
        <div className="flex-shrink-0 p-3 rounded-lg bg-gray-50" data-id="element-578">{icon}</div>
        <div className="ml-5" data-id="element-579">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-580">{title}</h3>
          <p className="text-2xl font-semibold text-gray-900" data-id="element-581">{value}</p>
        </div>
      </div>
      <div className="mt-4" data-id="element-582">
        <div className={`flex items-center text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-583">
          {isPositive ? <ArrowUpIcon className="h-4 w-4 mr-1" data-id="element-584" /> : <ArrowDownIcon className="h-4 w-4 mr-1" data-id="element-585" />}
          <span data-id="element-586">{change} from last month</span>
        </div>
      </div>
    </Card>;
};
export default Dashboard;